import { useState, useEffect } from "react";
import { Card, Badge, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faFolder,
  faFolderOpen,
  faCaretDown, 
  faCaretRight,
  faTag,
  faCalendarAlt,
  faUser
} from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";
import { DocumentItem } from "../DocumentItem/DocumentItem";
import { DocumentHierarchyDTO, DocumentDetails, Phase } from "types";

type Props = {
  document: DocumentHierarchyDTO;
  isExpanded: boolean;
  onToggleExpand: () => void;
};

const phaseColors = {
  [Phase.FB]: {
    bg: "#DEEBF5",
    text: "#1E40AF"
  },
  [Phase.MB]: {
    bg: "#E1EBDD",
    text: "#166534"
  },
  [Phase.EB]: {
    bg: "#FED5D2",
    text: "#991B1B"
  }
};

const getPhaseName = (phase: Phase): string => {
  switch (phase) {
    case Phase.FB:
      return "FB";
    case Phase.MB:
      return "MB";
    case Phase.EB:
      return "EB";
    default:
      return "Unknown";
  }
};

export const DocumentDetailsFolder = ({ document, isExpanded, onToggleExpand }: Props) => {
  const [expandedPhases, setExpandedPhases] = useState<Set<Phase>>(new Set());

  // Group children by phase
  const childrenByPhase = document.documentChildren.reduce((acc, child) => {
    const childDoc = child as DocumentDetails;
    if (!acc[childDoc.phase]) {
      acc[childDoc.phase] = [];
    }
    acc[childDoc.phase].push(childDoc);
    return acc;
  }, {} as Record<Phase, DocumentDetails[]>);

  // Initialize with all phases expanded by default
  useEffect(() => {
    if (document.documentChildren.length > 0) {
      // Get all unique phases from the children
      const phases = new Set<Phase>();
      document.documentChildren.forEach((child) => {
        const childDoc = child as DocumentDetails;
        phases.add(childDoc.phase);
      });
      
      setExpandedPhases(phases);
    }
  }, [document.documentChildren]);

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "yyyy-MM-dd HH:mm");
    } catch (error) {
      return dateString;
    }
  };

  const togglePhase = (phase: Phase) => {
    setExpandedPhases(prev => {
      const newSet = new Set(prev);
      if (newSet.has(phase)) {
        newSet.delete(phase);
      } else {
        newSet.add(phase);
      }
      return newSet;
    });
  };

  return (
    <Card className="rounded-3 shadow-sm border-0 mb-3">
      <Card.Body className="p-4">
        <div 
          className="d-flex justify-content-between align-items-center mb-3"
          onClick={onToggleExpand}
          style={{ cursor: 'pointer' }}
        >
          <div className="d-flex align-items-center gap-3">
            <FontAwesomeIcon 
              icon={isExpanded ? faFolderOpen : faFolder} 
              className="fs-4 text-primary"
            />
            <div>
              <h5 className="mb-0 fw-bold">{document.name || "Unnamed Folder"}</h5>
              <div className="text-muted small mt-1 d-flex align-items-center gap-2">
                <span className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                  {formatDate(document.createdDate)}
                </span>
                {document.createdBy && (
                  <span className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faUser} className="me-1" />
                    {document.createdBy}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="d-flex gap-2">
            {
              document.parentId !== null && document.parentId !== '00000000-0000-0000-0000-000000000000' && (
                <>
                  <Badge bg="primary" className="d-flex align-items-center py-2 px-3 rounded-pill">
                    <FontAwesomeIcon icon={faTag} className="me-1" />
                    {document.documentType || "No Type"}
                  </Badge>
                  <Badge 
                    bg={document.status === "Active" ? "success" : "secondary"}
                    className="d-flex align-items-center py-2 px-3 rounded-pill"
                  >
                    {document.status || "No Status"}
                  </Badge>
                </>
            )}
          </div>
        </div>

        {isExpanded && document.documentChildren.length > 0 && (
          <div className="mt-4 d-flex flex-column gap-3">
            {Object.entries(childrenByPhase).map(([phase, children]) => {
              const phaseEnum = Number(phase) as Phase;
              const phaseColor = phaseColors[phaseEnum];
              const isPhaseExpanded = expandedPhases.has(phaseEnum);

              return (
                <div key={phase}>
                  <Button
                    onClick={() => togglePhase(phaseEnum)}
                    className="w-100 text-start d-flex justify-content-between align-items-center mb-2 rounded-3 border-0 py-3 px-3"
                    style={{
                      backgroundColor: phaseColor.bg,
                      color: phaseColor.text,
                    }}
                  >
                    <span className="fw-bold d-flex align-items-center">
                      {getPhaseName(phaseEnum)} 
                      <span className="ms-2 fs-6 opacity-75">({children.length})</span>
                    </span>
                    <FontAwesomeIcon 
                      icon={isPhaseExpanded ? faCaretDown : faCaretRight}
                    />
                  </Button>
                  
                  <div className={`ps-4 d-flex flex-column gap-3 ${isPhaseExpanded ? '' : 'd-none'}`}>
                    {children.map((child) => (
                      <DocumentItem 
                        key={child.id}
                        document={child}
                        phase={child.phase}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}; 